/**
 * Building Management wire types — General Services' first module.
 *
 * **No person's NAME appears on any of them.** The responsible employee and whoever performed a
 * record travel as a `PersonnelId` plus the `PersonId` it resolves from; the profile asks
 * `personsApi.resolve` for the ones on screen, which applies the access level.
 *
 * **The two enums are NUMERIC on the wire** (no string-enum converter is configured), so each is
 * an `as const` object of numbers with the server's label beside it on the DTO. Facet values are
 * the exception and stay the enum NAMES.
 */

export const MAINTENANCE_RECURRENCE = {
  weekly: 0,
  monthly: 1,
  quarterly: 2,
  semiAnnual: 3,
  annual: 4,
} as const;

export type MaintenanceRecurrence = (typeof MAINTENANCE_RECURRENCE)[keyof typeof MAINTENANCE_RECURRENCE];

/** Mirrors `MaintenanceRecurrences.Label` — the picker needs the words before any row arrives. */
export const RECURRENCE_LABEL: Record<MaintenanceRecurrence, string> = {
  0: 'Weekly',
  1: 'Monthly',
  2: 'Quarterly',
  3: 'Semi-annual',
  4: 'Annual',
};

/** What KIND of activity was performed — mirrors `MaintenanceActivityType`. */
export const MAINTENANCE_ACTIVITY_TYPE = {
  maintenance: 0,
  compliance: 1,
  submission: 2,
} as const;

export type MaintenanceActivityType = (typeof MAINTENANCE_ACTIVITY_TYPE)[keyof typeof MAINTENANCE_ACTIVITY_TYPE];

/** Mirrors `MaintenanceActivityTypes.Label` — the picker needs the words before any row arrives. */
export const ACTIVITY_TYPE_LABEL: Record<MaintenanceActivityType, string> = {
  0: 'Maintenance',
  1: 'Compliance',
  2: 'Submission',
};

export const MAINTENANCE_AGING = {
  onSchedule: 0,
  nearExpiration: 1,
  expired: 2,
  expiredBeyondThreshold: 3,
} as const;

export type MaintenanceAging = (typeof MAINTENANCE_AGING)[keyof typeof MAINTENANCE_AGING];

/** The aging FACET's values — the enum names, as the server emits and parses them. */
export const AGING_FACET = {
  onSchedule: 'OnSchedule',
  nearExpiration: 'NearExpiration',
  expired: 'Expired',
  expiredBeyondThreshold: 'ExpiredBeyondThreshold',
} as const;

/**
 * Every entity type the module writes — what its History Logs filter on.
 *
 * ONE list both the index page and any other module-history surface read, so the two cannot
 * disagree about what the module touches. A record and the settings row are children the
 * parent's history would otherwise be blind to.
 */
export const BUILDING_MANAGEMENT_AUDIT_TYPES = [
  'MaintenanceItem',
  'MaintenanceRecord',
  'BuildingMaintenanceSettings',
];

// ── Categories ───────────────────────────────────────────────────────────────────────────

export interface MaintenanceCategoryListItem {
  id: string;
  name: string;
  description: string | null;
  /** The category's own window, or null where it falls back to the settings row. */
  nearExpirationDays: number | null;
  itemCount: number;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface MaintenanceCategoryOption {
  id: string;
  name: string;
}

export interface MaintenanceCategoryRequest {
  name: string;
  description: string | null;
  nearExpirationDays: number | null;
}

// ── Items ────────────────────────────────────────────────────────────────────────────────

export interface MaintenanceItemListItem {
  id: string;
  /** Server-assigned, as `BM-26001`. */
  referenceNumber: string;
  categoryId: string;
  categoryName: string;
  realPropertyId: string | null;
  /** Null only on an item registered before the building became required (12 Oct 2026). */
  buildingName: string | null;
  location: string | null;
  recurrence: MaintenanceRecurrence;
  recurrenceLabel: string;
  /** A calendar date, `YYYY-MM-DD` — never an instant. */
  dueDate: string;
  /** Negative once the due date has passed. Computed on the server's own "today". */
  daysUntilDue: number;
  aging: MaintenanceAging;
  agingLabel: string;
  lastPerformedOn: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface MaintenanceItemDetail extends MaintenanceItemListItem {
  buildingCode: string | null;
  /** One period after the due date — the server's arithmetic, so the form never repeats it. */
  suggestedNextDueDate: string;
  nearExpirationDays: number;
  nearExpirationFromCategory: boolean;
  overdueEscalationDays: number;
  responsiblePersonnelId: string | null;
  responsiblePersonId: string | null;
  supplierId: string | null;
  supplierName: string | null;
  remarks: string | null;
  recordCount: number;
}

export interface MaintenanceItemListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  category?: string;
  building?: string;
  aging?: string;
  recurrence?: string;
}

export interface MaintenanceItemRequest {
  categoryId: string | null;
  realPropertyId: string | null;
  location: string | null;
  recurrence: MaintenanceRecurrence | null;
  dueDate: string | null;
  responsiblePersonnelId: string | null;
  supplierId: string | null;
  remarks: string | null;
}

export interface MaintenanceBuildingOption {
  id: string;
  propertyCode: string;
  name: string;
}

// ── Records ──────────────────────────────────────────────────────────────────────────────

/** One ACTIVITY performed against an item. The CLR and wire names keep "record". */
export interface MaintenanceRecordListItem {
  id: string;
  maintenanceItemId: string;
  type: MaintenanceActivityType;
  typeLabel: string;
  activity: string;
  performedOn: string;
  performedByPersonnelId: string | null;
  performedByPersonId: string | null;
  /** Null where the activity did not renew the schedule. */
  previousDueDate: string | null;
  /** Null where the activity did not renew the schedule. */
  nextDueDate: string | null;
  remarks: string | null;
  attachmentRepositoryItemId: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface RecordMaintenanceRequest {
  type: MaintenanceActivityType | null;
  activity: string;
  performedOn: string | null;
  performedByPersonnelId: string | null;
  /** Present only when the activity RENEWS the schedule — the item's due date moves to it. */
  nextDueDate: string | null;
  remarks: string | null;
}

export interface MaintenanceRecordListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  /** Comma-separated activity-type NAMES (`Compliance`), as the facet emits them. */
  type?: string;
}

// ── Calendar ─────────────────────────────────────────────────────────────────────────────

/**
 * The calendar's filters — the REGISTER's search and facets, so the index page's Calendar view
 * honours the toolbar and rail it sits under. An absent `status` is live items only.
 */
export type MaintenanceCalendarFilters = Pick<
  MaintenanceItemListQuery,
  'search' | 'status' | 'category' | 'building' | 'aging' | 'recurrence'
>;

export interface MaintenanceCalendarEntry {
  maintenanceItemId: string;
  referenceNumber: string;
  categoryName: string;
  buildingName: string | null;
  location: string | null;
  date: string;
  /** False on the item's current due date; true on every occurrence projected after it. */
  isProjected: boolean;
  aging: MaintenanceAging | null;
  agingLabel: string | null;
}

// ── Settings ─────────────────────────────────────────────────────────────────────────────

export interface BuildingMaintenanceSettingsDetail {
  nearExpirationDays: number;
  overdueEscalationDays: number;
  /** "30 Days Expired", at the current threshold. */
  escalatedLabel: string;
  createdAt: string | null;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface BuildingMaintenanceSettingsRequest {
  nearExpirationDays: number | null;
  overdueEscalationDays: number | null;
}
